/**
 * Documents service — the project-document API surface.
 *
 * `download_url` is a TEMPORARY SIGNED URL the backend mints against an
 * S3-compatible bucket (STORAGE-1, #71 — it used to say GCS here, and that
 * had been wrong since that landed). Nothing in this module or its callers
 * ever constructs one: no bucket name, no endpoint, no object key. When the
 * backend cannot mint a link it returns the row with `download_url: null`,
 * which means the LINK is absent, not the document.
 */
import apiClient from './api';

const documentsService = {
  async getReviewQueue(params = {}) {
    const response = await apiClient.get('/documents/review-queue', { params });
    return response.data;
  },
  /**
   * Get all documents for a project
   * @param {string} projectId
   * @param {string} category - Optional category filter
   * @returns {Promise<{documents: Array, total: number}>}
   */
  async getAll(projectId, category = null) {
    const params = category ? { category } : {};
    const response = await apiClient.get(`/projects/${projectId}/documents`, { params });
    return response.data;
  },

  /**
   * Get a single document with download URL
   * @param {string} projectId
   * @param {string} documentId
   * @returns {Promise<object>}
   */
  async getById(projectId, documentId) {
    const response = await apiClient.get(`/projects/${projectId}/documents/${documentId}`);
    return response.data;
  },

  /**
   * Upload a document
   * @param {string} projectId
   * @param {File} file
   * @param {object} metadata - { category, description }
   * @param {function} onProgress - Progress callback (0-100)
   * @returns {Promise<object>}
   */
  async upload(projectId, file, metadata = {}, onProgress = null) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('category', metadata.category || 'general');
    if (metadata.description) {
      formData.append('description', metadata.description);
    }
    if (metadata.status) {
      formData.append('status', metadata.status);
    }
    if (metadata.review_status) {
      formData.append('review_status', metadata.review_status);
    }

    const response = await apiClient.post(
      `/projects/${projectId}/documents`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            onProgress(percentCompleted);
          }
        },
      }
    );
    return response.data;
  },

  /**
   * Update document workflow metadata
   * @param {string} projectId
   * @param {string} documentId
   * @param {object} updates
   * @returns {Promise<object>}
   */
  async update(projectId, documentId, updates) {
    const response = await apiClient.put(
      `/projects/${projectId}/documents/${documentId}`,
      updates
    );
    return response.data;
  },

  /**
   * Delete a document
   * @param {string} projectId
   * @param {string} documentId
   * @returns {Promise<object>}
   */
  async delete(projectId, documentId) {
    const response = await apiClient.delete(
      `/projects/${projectId}/documents/${documentId}`
    );
    return response.data;
  },

  /**
   * Open the backend's own temporary signed URL. Used verbatim.
   * @param {string} downloadUrl - Signed URL from the document payload
   */
  download(downloadUrl) {
    window.open(downloadUrl, '_blank');
  },
};

export default documentsService;
